import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, Ban, Check, ArrowLeft, Users, CalendarDays, Flame, Flag } from "lucide-react";
import { toast } from "sonner";
import { CallQualityPanel } from "@/components/CallQualityPanel";
import { TestersPanel } from "@/components/TestersPanel";
import { LocationsPanel } from "@/components/LocationsPanel";
import { WaitlistPanel } from "@/components/WaitlistPanel";
import { EventImportPanel } from "@/components/EventImportPanel";
import { CheckinsPanel } from "@/components/CheckinsPanel";

const CATS = ["concert", "sports", "gallery", "mixer", "nightlife", "comedy", "theatre", "movie"];
const CITIES = ["London", "Barcelona", "Tokyo", "New York", "Milan", "Seoul"];
const empty = { title: "", category: "concert", venue: "", city: "London", country: "UK", date_time: "", cover_image: "", description: "" };
const C2 = { London: "UK", Barcelona: "Spain", Tokyo: "Japan", "New York": "USA", Milan: "Italy", Seoul: "South Korea" };

export default function Admin() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const { data: stats } = useQuery({ queryKey: ["admin-stats"], queryFn: async () => (await api.get("/admin/stats")).data });
  const { data: events = [] } = useQuery({ queryKey: ["admin-events"], queryFn: async () => (await api.get("/events", { params: { scope: "global" } })).data });
  const { data: reports = [] } = useQuery({ queryKey: ["admin-reports"], queryFn: async () => (await api.get("/admin/reports")).data });

  const save = async () => {
    if (!form.title || !form.venue || !form.date_time || !form.cover_image) { toast.error("Fill all fields"); return; }
    const payload = { ...form, country: C2[form.city] || form.country };
    try {
      if (editId) await api.put(`/admin/events/${editId}`, payload);
      else await api.post("/admin/events", payload);
      qc.invalidateQueries({ queryKey: ["admin-events"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] });
      setOpen(false); setForm(empty); setEditId(null);
      toast.success(editId ? "Event updated" : "Event created");
    } catch { toast.error("Could not save event"); }
  };

  const del = async (id) => {
    await api.delete(`/admin/events/${id}`);
    qc.invalidateQueries({ queryKey: ["admin-events"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] });
    toast.success("Event deleted");
  };

  const ban = async (id) => { await api.post(`/admin/ban/${id}`); qc.invalidateQueries({ queryKey: ["admin-reports"] }); toast.success("User banned"); };
  const resolve = async (id) => { await api.post(`/admin/reports/${id}/resolve`); qc.invalidateQueries({ queryKey: ["admin-reports"] }); };

  const toLocalInput = (v) => {
    const d = v ? new Date(v) : new Date();
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
  };

  const STAT = [
    { label: "Users", value: stats?.users, icon: Users },
    { label: "Events", value: stats?.events, icon: CalendarDays },
    { label: "Matches", value: stats?.matches, icon: Flame },
    { label: "Open reports", value: stats?.open_reports, icon: Flag },
  ];

  return (
    <div className="min-h-screen">
      <header className="glass border-b border-amber-500/10 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size="text-xl" to="/discover" />
            <span className="chip text-[11px] px-2.5 py-0.5 rounded-full">Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => navigate("/discover")} variant="ghost" className="rounded-full text-emerald-100/70"><ArrowLeft className="h-4 w-4 mr-2" /> App</Button>
            <Button onClick={async () => { await logout(); navigate("/"); }} variant="ghost" className="rounded-full text-pink-300">Logout</Button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5 py-8">
        <h1 className="font-display font-black text-3xl">Welcome, {user?.name}</h1>
        <Link to="/admin/control" data-testid="admin-control-link" className="inline-flex mt-2 text-sm font-semibold text-blue-400 hover:text-blue-300">Open Admin Control →</Link>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          {STAT.map((s) => (
            <div key={s.label} className="glass rounded-2xl p-5">
              <s.icon className="h-5 w-5 text-amber-400" />
              <p className="font-display font-black text-3xl mt-3">{s.value ?? "—"}</p>
              <p className="text-xs text-emerald-100/60 uppercase tracking-wide">{s.label}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue={location.state?.tab || "events"} className="mt-8">
          <TabsList className="bg-white/5 rounded-full">
            <TabsTrigger value="events" data-testid="tab-events" className="rounded-full">Events</TabsTrigger>
            <TabsTrigger value="quality" data-testid="tab-call-quality" className="rounded-full">Call quality</TabsTrigger>
            <TabsTrigger value="testers" data-testid="tab-testers" className="rounded-full">Testers</TabsTrigger>
            <TabsTrigger value="areas" data-testid="tab-areas" className="rounded-full">Launch areas</TabsTrigger>
            <TabsTrigger value="waitlist" data-testid="tab-waitlist" className="rounded-full">Waitlist</TabsTrigger>
            <TabsTrigger value="checkins" data-testid="tab-checkins" className="rounded-full">Date check-ins</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports" className="rounded-full">Reports {reports.filter(r => r.status === "open").length > 0 && `(${reports.filter(r => r.status === "open").length})`}</TabsTrigger>
          </TabsList>

          <TabsContent value="events" className="mt-6">
            <EventImportPanel />
            <div className="flex justify-end mb-4">
              <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty); setEditId(null); } }}>
                <DialogTrigger asChild>
                  <Button data-testid="new-event-btn" onClick={() => { setForm({ ...empty, date_time: toLocalInput() }); setEditId(null); }} className="btn-gold rounded-full"><Plus className="h-4 w-4 mr-2" /> New event</Button>
                </DialogTrigger>
                <DialogContent className="glass border-amber-500/20 max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle className="font-display">{editId ? "Edit event" : "Create event"}</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Title</Label><Input data-testid="ev-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 bg-white/5 border-white/10" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Category</Label>
                        <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                          <SelectTrigger data-testid="ev-category" className="mt-1 bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                          <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label>City</Label>
                        <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v })}>
                          <SelectTrigger data-testid="ev-city" className="mt-1 bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                          <SelectContent>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div><Label>Venue</Label><Input data-testid="ev-venue" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} className="mt-1 bg-white/5 border-white/10" /></div>
                    <div><Label>Date & time</Label><Input data-testid="ev-date" type="datetime-local" value={toLocalInput(form.date_time)} onChange={(e) => setForm({ ...form, date_time: new Date(e.target.value).toISOString() })} className="mt-1 bg-white/5 border-white/10" /></div>
                    <div><Label>Cover image URL</Label><Input data-testid="ev-cover" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} className="mt-1 bg-white/5 border-white/10" placeholder="https://…" /></div>
                    <div><Label>Description</Label><Textarea data-testid="ev-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-white/5 border-white/10" /></div>
                    <Button data-testid="ev-save" onClick={save} className="w-full btn-gold rounded-full h-11">{editId ? "Update" : "Create"}</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div className="space-y-2">
              {events.map((ev) => (
                <div key={ev.id} className="glass rounded-2xl p-3 flex items-center gap-4">
                  <img src={imgUrl(ev.cover_image)} alt="" className="h-14 w-14 rounded-xl object-cover" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold truncate">{ev.title}</h3>
                    <p className="text-xs text-emerald-100/60">{ev.category} · {ev.city} · {ev.attendee_count} interested</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => { setForm({ title: ev.title, category: ev.category, venue: ev.venue, city: ev.city, country: ev.country, date_time: ev.date_time, cover_image: ev.cover_image, description: ev.description }); setEditId(ev.id); setOpen(true); }} className="rounded-full">Edit</Button>
                  <Button data-testid={`del-event-${ev.id}`} size="icon" variant="ghost" onClick={() => del(ev.id)} className="rounded-full text-pink-300"><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="reports" className="mt-6">
            {reports.length === 0 ? (
              <div className="glass rounded-2xl p-10 text-center text-emerald-100/60">No reports. The community is behaving ✨</div>
            ) : (
              <div className="space-y-2">
                {reports.map((r) => (
                  <div key={r.id} className="glass rounded-2xl p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold">{r.reported_name} <span className="text-xs text-emerald-100/50">reported by {r.reporter_name}</span></p>
                        <p className="text-sm text-pink-200 mt-0.5">{r.reason}</p>
                        {r.details && <p className="text-sm text-emerald-100/60 mt-1">{r.details}</p>}
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${r.status === "open" ? "bg-amber-500/20 text-amber-200" : "bg-emerald-500/20 text-emerald-200"}`}>{r.status}</span>
                    </div>
                    <div className="flex gap-2 mt-3">
                      {!r.reported_banned && <Button data-testid={`ban-${r.reported_id}`} size="sm" onClick={() => ban(r.reported_id)} className="btn-pink rounded-full h-9"><Ban className="h-4 w-4 mr-1.5" /> Ban user</Button>}
                      {r.reported_banned && <span className="text-xs text-pink-300 flex items-center gap-1"><Ban className="h-3 w-3" /> Banned</span>}
                      {r.status === "open" && <Button size="sm" variant="outline" onClick={() => resolve(r.id)} className="rounded-full h-9 border-white/15"><Check className="h-4 w-4 mr-1.5" /> Resolve</Button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value="quality" className="mt-6"><CallQualityPanel /></TabsContent>
          <TabsContent value="testers" className="mt-6"><TestersPanel /></TabsContent>
          <TabsContent value="areas" className="mt-6"><LocationsPanel /></TabsContent>
          <TabsContent value="waitlist" className="mt-6"><WaitlistPanel /></TabsContent>
          <TabsContent value="checkins" className="mt-6"><CheckinsPanel /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
